import { Link, useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
  IconArrowRight,
  IconCheck,
  IconLoader2,
  IconSparkles,
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { usePostHog } from 'posthog-js/react';
import { useCFAuth } from '@/lib/auth/cf-auth-context';
import { getSessionHeaders } from '@/lib/cloudflare/auth-client';
import {
  buildGettingStartedPath,
  hasBillingAccess,
  startTrialCheckout,
} from '@/lib/cloudflare/billing-client';
import type { BillingInterval, PlanType, Subscription } from '@/types/subscription';
import { isActiveSubscription } from '@/types/subscription';

const CF_WORKER_URL = import.meta.env.VITE_CLOUDFLARE_WORKER_URL || '';
const PREP_STEP_DELAY_MS = 900;

const PREP_STEPS = [
  {
    title: 'Gmail connected',
    description: 'Your account is linked and ready to read mailbox structure.',
  },
  {
    title: 'Reading labels and filters',
    description: 'We are using your existing setup to shape the first cleanup pass.',
  },
  {
    title: 'Preparing the first cleanup pass',
    description: 'One billing step will unlock the full sync and inbox cleanup.',
  },
] as const;

const PRO_PRICE_COPY: Record<BillingInterval, { price: string; helper: string }> = {
  monthly: {
    price: '$19/mo after trial',
    helper: 'Best for getting started',
  },
  yearly: {
    price: '$108/yr after trial',
    helper: 'Lower effective monthly price',
  },
};

const BUSINESS_PRICE_COPY: Record<BillingInterval, { price: string; helper: string }> = {
  monthly: {
    price: 'Business monthly after trial',
    helper: 'Shared inbox workflows for teams',
  },
  yearly: {
    price: 'Business yearly after trial',
    helper: 'Annual billing for team rollout',
  },
};

interface GettingStartedPageProps {
  success?: boolean;
  canceled?: boolean;
  initialPlan?: PlanType;
  initialInterval?: BillingInterval;
}

export function GettingStartedPage({
  success,
  canceled,
  initialPlan = 'pro',
  initialInterval = 'monthly',
}: GettingStartedPageProps) {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { status, user } = useCFAuth();
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(initialPlan);
  const [selectedInterval, setSelectedInterval] = useState<BillingInterval>(initialInterval);
  const [completedStepCount, setCompletedStepCount] = useState(success || canceled ? PREP_STEPS.length : 1);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const successToastShownRef = useRef(false);
  const canceledToastShownRef = useRef(false);
  const signUpEventFiredRef = useRef(false);

  // Fire sign_up GTM event for new users
  useEffect(() => {
    if (status === 'authenticated' && user?.id && !signUpEventFiredRef.current) {
      const storageKey = `wb_signup_fired_${user.id}`;
      if (!localStorage.getItem(storageKey)) {
        // Only fire once per user
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'sign_up',
          method: 'google',
          user_id: user.id
        });
        localStorage.setItem(storageKey, 'true');
      }
      signUpEventFiredRef.current = true;
    }
  }, [status, user]);

  useEffect(() => {
    setSelectedPlan(initialPlan);
  }, [initialPlan]);

  useEffect(() => {
    setSelectedInterval(initialInterval);
  }, [initialInterval]);

  useEffect(() => {
    setCompletedStepCount(success || canceled ? PREP_STEPS.length : 1);
  }, [success, canceled, user?.id]);

  useEffect(() => {
    if (status !== 'authenticated' || success || canceled) {
      return;
    }

    const timeouts = PREP_STEPS.slice(1).map((_, index) => window.setTimeout(() => {
      setCompletedStepCount((current) => Math.max(current, index + 2));
    }, PREP_STEP_DELAY_MS * (index + 1)));

    return () => {
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [status, success, canceled, user?.id]);

  useEffect(() => {
    if (!success || successToastShownRef.current) {
      return;
    }

    successToastShownRef.current = true;
    toast.success('Your 30-day free trial is active.');
    posthog.capture('trial_started', { plan: selectedPlan, interval: selectedInterval });
  }, [success]);

  useEffect(() => {
    if (!canceled || canceledToastShownRef.current) {
      return;
    }

    canceledToastShownRef.current = true;
    toast.info('Setup paused. You can restart the trial whenever you are ready.');
    posthog.capture('checkout_canceled', { plan: selectedPlan, interval: selectedInterval });
  }, [canceled]);

  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }

    let isActive = true;
    let timeoutId: number | null = null;

    const pollSubscription = async () => {
      try {
        const response = await fetch(`${CF_WORKER_URL}/api/subscription`, {
          credentials: 'include',
          headers: getSessionHeaders(),
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        if (!isActive) {
          return;
        }

        setSubscription((data?.data?.subscription || data?.subscription || null) as Subscription | null);
      } catch (error) {
        console.error('Failed to check onboarding subscription status:', error);
      } finally {
        if (!isActive) {
          return;
        }

        timeoutId = window.setTimeout(
          pollSubscription,
          success ? 2000 : 10000
        );
      }
    };

    void pollSubscription();

    return () => {
      isActive = false;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [status, success, user?.id]);

  const planLabel = selectedPlan === 'business' ? 'Business' : 'Pro';
  const planDescription = selectedPlan === 'business'
    ? 'Shared inbox cleanup and routing for teams.'
    : 'Personal inbox cleanup with rules and automation.';
  const priceCopy = selectedPlan === 'business'
    ? BUSINESS_PRICE_COPY[selectedInterval]
    : PRO_PRICE_COPY[selectedInterval];
  const isSubscribed = isActiveSubscription(subscription) || hasBillingAccess(user?.subscriptionStatus);
  const isVerifyingSubscription = Boolean(success) && !isSubscribed;
  const trialEndsLabel = subscription?.trialEndsAt
    ? format(new Date(subscription.trialEndsAt * 1000), 'PPP')
    : null;
  const isReadyForCheckout = completedStepCount >= PREP_STEPS.length || canceled;

  useEffect(() => {
    if (status === 'unauthenticated') {
      navigate({
        to: '/auth/signin',
        search: {
          callbackUrl: buildGettingStartedPath({ plan: selectedPlan, interval: selectedInterval }),
        },
        replace: true,
      });
    }
  }, [navigate, selectedInterval, selectedPlan, status]);

  useEffect(() => {
    if (!isSubscribed) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      navigate({ to: '/', replace: true });
    }, success ? 1000 : 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isSubscribed, navigate, success]);

  const handleStartTrial = async () => {
    setIsStartingCheckout(true);
    posthog.capture('started_trial_checkout', { plan: selectedPlan, interval: selectedInterval });

    try {
      const checkoutUrl = await startTrialCheckout({
        plan: selectedPlan,
        interval: selectedInterval,
        successPath: buildGettingStartedPath({
          success: true,
          plan: selectedPlan,
          interval: selectedInterval,
        }),
        cancelPath: buildGettingStartedPath({
          canceled: true,
          plan: selectedPlan,
          interval: selectedInterval,
        }),
      });

      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to start onboarding checkout:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
      setIsStartingCheckout(false);
    }
  };

  const headerCopy = useMemo(() => {
    if (isSubscribed) {
      return {
        eyebrow: 'Trial active',
        title: 'Opening your inbox',
        description: trialEndsLabel
          ? `Your ${planLabel} trial is active through ${trialEndsLabel}. We are taking you into the inbox now.`
          : 'Your trial is active. We are taking you into the inbox now.',
      };
    }

    if (isVerifyingSubscription) {
      return {
        eyebrow: 'Verifying subscription',
        title: 'Finishing your trial setup',
        description: 'Your checkout completed. We are confirming the subscription and will open the inbox as soon as Stripe finishes syncing.',
      };
    }

    if (canceled) {
      return {
        eyebrow: 'Setup paused',
        title: 'Resume when you are ready',
        description: 'Gmail is already connected. Start the trial when you want to kick off the first full cleanup pass.',
      };
    }

    if (isReadyForCheckout) {
      return {
        eyebrow: 'Inbox connected',
        title: 'Your first cleanup pass is ready',
        description: 'We have connected Gmail and prepared the workspace. Start the 30-day trial to launch the full sync and open the app.',
      };
    }

    return {
      eyebrow: 'Connecting Gmail',
      title: 'Preparing your inbox',
      description: 'We are connecting Gmail, reading labels, and getting the first cleanup pass ready before we ask for billing.',
    };
  }, [canceled, isReadyForCheckout, isSubscribed, isVerifyingSubscription, planLabel, trialEndsLabel]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#fafafa]">
        <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm text-slate-600 shadow-sm">
          <IconLoader2 className="h-4 w-4 animate-spin" />
          Checking your inbox setup...
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#fafafa] px-6 py-10 text-slate-950">
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between pb-8">
        <Link to="/" className="inline-flex items-center gap-3">
          <img src="/logo.svg" alt="Winbox Zero" className="h-7 w-auto" />
          <span className="text-sm font-medium text-slate-600">Winbox Zero</span>
        </Link>
        <Link to="/pricing" className="text-sm text-slate-500 transition-colors hover:text-slate-900">
          Pricing
        </Link>
      </div>

      <div className="mx-auto grid w-full max-w-5xl gap-8 lg:grid-cols-[1.2fr_0.8fr]">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm lg:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-blue-700">
            <IconSparkles className="h-3.5 w-3.5" />
            {headerCopy.eyebrow}
          </div>

          <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
            {headerCopy.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
            {headerCopy.description}
          </p>

          <div className="mt-10 space-y-4">
            {PREP_STEPS.map((step, index) => {
              const isComplete = index < completedStepCount || isSubscribed;
              return (
                <div
                  key={step.title}
                  className={`flex items-start gap-4 rounded-2xl border px-4 py-4 transition-all ${
                    isComplete
                      ? 'border-emerald-200 bg-emerald-50/70'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                    isComplete ? 'bg-emerald-600 text-white' : 'bg-white text-slate-400 ring-1 ring-slate-200'
                  }`}>
                    {isComplete ? <IconCheck className="h-4 w-4" /> : <IconLoader2 className="h-4 w-4 animate-spin" />}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-950">{step.title}</div>
                    <p className="mt-1 text-sm leading-6 text-slate-600">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="rounded-[2rem] border border-slate-200 bg-slate-950 p-8 text-white shadow-sm lg:p-10">
          <div className="text-sm font-medium text-slate-300">Trial plan</div>
          <div className="mt-3 text-3xl font-semibold">{planLabel}</div>
          <p className="mt-2 text-sm leading-6 text-slate-300">{planDescription}</p>

          <div className="mt-8 flex items-center gap-1 rounded-full bg-white/10 p-1">
            <button
              type="button"
              onClick={() => setSelectedInterval('monthly')}
              className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
                selectedInterval === 'monthly'
                  ? 'bg-white text-slate-950'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setSelectedInterval('yearly')}
              className={`flex-1 rounded-full px-4 py-2 text-sm font-semibold transition ${
                selectedInterval === 'yearly'
                  ? 'bg-white text-slate-950'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Yearly
            </button>
          </div>

          <div className="mt-8 rounded-3xl bg-white/8 p-5">
            <div className="text-sm font-medium text-slate-300">30 days free</div>
            <div className="mt-2 text-3xl font-semibold text-white">{priceCopy.price}</div>
            <p className="mt-2 text-sm text-slate-300">{priceCopy.helper}</p>
            <p className="mt-4 text-xs leading-5 text-slate-400">
              No charge until the trial ends. We ask for billing right before the first full sync and cleanup pass starts.
            </p>
          </div>

          <button
            type="button"
            onClick={handleStartTrial}
            disabled={!isReadyForCheckout || isStartingCheckout || isSubscribed || isVerifyingSubscription}
            className="mt-8 inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubscribed
              ? 'Opening inbox...'
              : isVerifyingSubscription
                ? 'Verifying subscription...'
              : isStartingCheckout
                ? 'Opening secure checkout...'
                : 'Start 30-day free trial'}
            {!isSubscribed && !isStartingCheckout && !isVerifyingSubscription && <IconArrowRight className="ml-2 h-4 w-4" />}
          </button>
        </aside>
      </div>
    </div>
  );
}
